/**
 * Ends an authenticated session and carries the reason to the login screen
 * (RR-330).
 *
 * The login screen is mounted outside DefaultLayout, so the notice banner
 * cannot reach it. LoginComponent reads `?reason=<key>` instead and resolves it
 * against `notices.login_reasons`. This module and ResetPasswordComponent are
 * the only producers of that parameter: before them the two keys shipped in all
 * eight locales were unreachable, so a mass revoke (RR-327) or a forced reset
 * (RR-318) dropped people on an unexplained login form.
 */

/**
 * Keys under `notices.login_reasons`. Adding one here means adding the copy to
 * resources/lang/{locale}/notices.php for all 8 locales; an unknown key renders
 * nothing rather than throwing.
 */
export const LoginReason = {
  SESSION_REVOKED: "session_revoked",
  PASSWORD_CHANGED: "password_changed"
};

export const loginLocation = reason => ({
  name: "login",
  query: reason ? { reason } : {}
});

const isLoginRoute = route => route?.name === "login";

const hasLoginReason = (route, reason) => {
  if (!isLoginRoute(route)) return false;
  if (!reason) return true;

  return route.query?.reason === reason;
};

/**
 * One navigation to login, and never while the tree is already leaving
 * (RR-363).
 *
 * Session revoke used to fire router.push from endSession *and* from the
 * root mounted() hook, while the in-flight authenticated route was still
 * resolving. Vue Router then rejected with "Redirected when going from …
 * via a navigation guard", which Vue reported after the layout unmounted.
 *
 * replace, not push: the authenticated page must not stay in history for
 * Back to revive. No-op when we are already on (or going to) login with
 * the same reason, so a second 401 cannot start a competing transition.
 */
export const redirectToLogin = (router, reason) => {
  if (!router || isCredentialPath()) return Promise.resolve();

  const current = router.currentRoute;
  const pending = router.history?.pending;

  if (hasLoginReason(current, reason) || hasLoginReason(pending, reason)) {
    return Promise.resolve();
  }

  return router.replace(loginLocation(reason)).catch(() => {});
};

/**
 * Pages whose URL *is* the credential.
 *
 * A reset link arrives as /reset-password?token=...&email=..., is single-use and
 * cannot be re-opened. Anything that navigates away from it throws those two
 * parameters away, and the user is left on /login with a link that no longer
 * works - which is how "resetting my password is impossible" is reported.
 *
 * So none of the session machinery may redirect or reload these pages. Their
 * whole point is that the visitor has no working session; acting on the dead one
 * left behind in localStorage can only get in the way.
 *
 * Matched on the URL rather than on the route name because the bootstrap runs
 * from the root component's mounted() hook, at which point vue-router's initial
 * transition has not resolved yet and router.currentRoute is still START.
 */
const CREDENTIAL_PATHS = ["/reset-password", "/forgot-password"];

export const isCredentialPath = (pathname = window.location.pathname) =>
  CREDENTIAL_PATHS.indexOf(String(pathname).replace(/\/+$/, "") || "/") > -1;

/**
 * Endpoints that answer 401 as a normal outcome rather than as a dead session.
 *
 * A wrong password on the login form is a 401, and announcing "you have been
 * signed out for security reasons" there would be a lie. The refresh-token call
 * goes to the same URL, and its failure is already handled by handleAuth, which
 * produces the reason itself. The two password endpoints are here for the same
 * reason: they are what someone without a session calls, and their failures are
 * rendered on the form that made them.
 */
const AUTH_ENDPOINTS = [
  "/login",
  "/oauth/token",
  "/oauth/token/revoke",
  "/password/reset",
  "/password/email"
];

const isAuthEndpoint = url => {
  if (!url) return false;

  const path = String(url).split("?")[0].replace(/\/$/, "");

  return AUTH_ENDPOINTS.some(endpoint => path.endsWith(endpoint));
};

/**
 * One redirect per session end, however many requests were in flight when the
 * token died.
 */
let ending = false;

export const endSession = (store, router, reason = LoginReason.SESSION_REVOKED) => {
  if (ending) return Promise.resolve();

  ending = true;

  // logoutOffline rejects when there is no token left to revoke. The user is
  // out either way, so the redirect must not depend on it succeeding.
  return Promise.resolve(store.dispatch("logoutOffline"))
    .catch(() => {})
    // Dropping the dead session is right everywhere; announcing it on the login
    // screen is not. On a credential page the user is already dealing with the
    // fact that they cannot get in, and the redirect would take the reset token
    // in the URL with it.
    .then(() => redirectToLogin(router, reason))
    .catch(() => {})
    .then(() => {
      ending = false;
    });
};

/**
 * Turns a 401 on any authenticated call into an explained logout.
 *
 * vue-authenticate installs a response interceptor without a rejection handler,
 * so until now a revoked token mid-session did nothing at all: Service.js does
 * not handle 401 either, and the user kept clicking around a shell that had
 * stopped answering.
 */
export const installSessionExpiryHandler = (store, router) => {
  axios.interceptors.response.use(null, error => {
    const status = error?.response?.status;

    if (
      status === 401 &&
      store.getters.isAuthenticated &&
      !isAuthEndpoint(error?.config?.url)
    ) {
      endSession(store, router);
    }

    return Promise.reject(error);
  });
};
